import React, { Fragment, useReducer, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import * as Sentry from "@sentry/react";

import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const EVOJETS_ORG_ID = "600c8d371f10c61723e64402";
const DSA_ROLE_NAME = "Dynamic Sales Assistant";

// accountDirectorId is only ever consumed for Sales Assistant users
// (lead routing, quotes, dashboards), so only they require one; Account
// Executives may optionally have one. All other roles never need an AD.
const AD_REQUIRED_ROLE_NAMES = ["Sales Assistant"];
const AD_OPTIONAL_ROLE_NAMES = ["Account Executive"];

// Sales roles need an Outreach seat; Super Admins and other roles don't
// (the field stays visible as optional for them).
const OUTREACH_REQUIRED_ROLE_NAMES = [
  "Account Director",
  "Account Executive",
  "Sales Assistant",
  DSA_ROLE_NAME,
];

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const NewUserContent = (props) => {
  const history = useHistory();

  const initialState = {
    organizations: [],
    availableRoles: [],
    availableAccountDirectors: [],
    availableOutreachUsers: [],
    selectedOrganization: {},
    selectedOutreachUser: {},
    selectedAccountDirector: null,
    parentUsers: [],
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    selectedRole: { id: "", value: "", label: "" },
    isSubmitting: false,
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    document.title = "New User | evoJets HQ";
    loadOrganizations();
  }, []);

  const loadOrganizations = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let organizations = await ai
      .auth(token)
      .get(`/api/organizations/list`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    let roles = await ai
      .auth(token)
      .get(`/api/users/availableRoles`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    let accountDirectors = await ai
      .auth(token)
      .get(`/api/users/roles/accountDirectors`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    let outreachUsers = await ai
      .auth(token)
      .get(`/api/users/outreachUsers`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (outreachUsers?.data?.data?.length) {
      //account directors loaded
      outreachUsers = outreachUsers.data.data.map((user) => {
        return {
          label: user?.attributes?.name,
          value: user.id,
        };
      });

      dispatch({
        type: "fieldChanged",
        value: outreachUsers,
        field: "availableOutreachUsers",
      });
    }

    if (accountDirectors?.data?.users?.length) {
      //account directors loaded
      accountDirectors = accountDirectors.data.users.map((ad) => {
        return {
          ...ad,
          label: ad?.name,
          value: ad?.user_id,
        };
      });

      dispatch({
        type: "fieldChanged",
        value: accountDirectors,
        field: "availableAccountDirectors",
      });
    }

    if (
      organizations &&
      organizations.data &&
      organizations.data.organizations &&
      organizations.data.organizations.length > 0
    ) {
      let organizationsFormatted = organizations.data.organizations.map(
        (organization) => {
          return {
            ...organization,
            label: organization.name,
            value: organization._id,
          };
        }
      );

      dispatch({
        type: "fieldChanged",
        field: "organizations",
        value: organizationsFormatted,
      });
    }

    if (
      roles &&
      roles.data &&
      roles.data.roles &&
      roles.data.roles.length > 0
    ) {
      dispatch({
        type: "fieldChanged",
        field: "availableRoles",
        value: roles.data.roles.map((role) => {
          return { ...role, label: role.name, value: role.id };
        }),
      });
    }
  };

  // Shared by the submit checks and the JSX field gates below so a required
  // field can never be hidden.
  const selectedRoleName = state.selectedRole?.name;
  const isEvoOrg = state.selectedOrganization?._id === EVOJETS_ORG_ID;
  const accountDirectorRequired =
    isEvoOrg && AD_REQUIRED_ROLE_NAMES.includes(selectedRoleName);
  const showAccountDirectorField =
    accountDirectorRequired ||
    (isEvoOrg && AD_OPTIONAL_ROLE_NAMES.includes(selectedRoleName));
  const outreachUserRequired =
    isEvoOrg && OUTREACH_REQUIRED_ROLE_NAMES.includes(selectedRoleName);
  const showParentUsersField = isEvoOrg && selectedRoleName === DSA_ROLE_NAME;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (state.isSubmitting) {
      return;
    }

    if (!state.selectedRole?.id) {
      return toast.error("Please select a user role.", toastOptions);
    }

    let formData = {
      firstName: state.firstName,
      lastName: state.lastName,
      name: `${state.firstName} ${state.lastName}`,
      phone: state.phone,
      email: state.email,
      organizationId: state.selectedOrganization._id,
      roles: [state.selectedRole.id],
    };

    if (isEvoOrg) {
      if (state?.selectedOutreachUser?.value) {
        formData.outreachUserId = state.selectedOutreachUser.value;
      } else if (outreachUserRequired) {
        return toast.error("Please select an Outreach User.", toastOptions);
      }
    }

    if (showAccountDirectorField) {
      if (state?.selectedAccountDirector?.value) {
        formData.accountDirectorId = state.selectedAccountDirector.value;
      } else if (accountDirectorRequired) {
        return toast.error("Please select an Account Director.", toastOptions);
      }
    }

    if (showParentUsersField) {
      formData.parentUserIds = (state?.parentUsers || [])
        .map((item) => item?.user_id)
        .filter(Boolean);

      if (!formData.parentUserIds.length) {
        return toast.error(
          "Please select at least one Parent User.",
          toastOptions
        );
      }
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({ type: "fieldChanged", field: "isSubmitting", value: true });

    try {
      await ai.auth(token).post(`/api/users/create`, formData);

      toast.success("User Created Successfully!", toastOptions);
    } catch (error) {
      // Status only — the axios error carries the submitted form data.
      Sentry.captureMessage(
        `POST /api/users/create failed: ${error?.response?.status ?? "network"}`
      );

      toast.error(
        getApiErrorMessage(error, "Failed to create user."),
        toastOptions
      );
    } finally {
      dispatch({ type: "fieldChanged", field: "isSubmitting", value: false });
    }
  };

  return (
    <Fragment>
      <h2 id="addAirportHeading">New User</h2>
      <form id="" className="base-form two-column" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            name="firstName"
            required
            value={state.firstName}
            placeholder="First Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            name="lastName"
            required
            value={state.lastName}
            placeholder="Last Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="text"
            name="phone"
            value={state.phone}
            placeholder="Phone Number"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            name="email"
            required
            value={state.email}
            placeholder="Email Address"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="notificationEmails">Organization</label>

          <Select
            key={`organization-options-key-${JSON.stringify(
              state.organizations
            )}`}
            className="organization-select"
            classNamePrefix="select"
            options={state.organizations}
            // defaultValue={props.initialTags}
            placeholder="Organization"
            onChange={(selectedOrganization) => {
              dispatch({
                type: "fieldChanged",
                field: "selectedOrganization",
                value: selectedOrganization,
              });
            }}
          ></Select>
        </div>

        {/* No password field: the server generates a throwaway and the user
            sets their real password via the welcome email's reset link. */}

        <div className="form-group">
          <label htmlFor="userRoles">User Role</label>

          <Select
            key={`role-options-key-${JSON.stringify(state.availableRoles)}`}
            name="userRoles"
            className="organization-select"
            classNamePrefix="select"
            options={state.availableRoles}
            // defaultValue={state.userRoles}
            placeholder="User Role"
            onChange={(selectedRole) => {
              dispatch({
                type: "fieldChanged",
                field: "selectedRole",
                value: selectedRole,
              });
            }}
          ></Select>
        </div>

        {showAccountDirectorField && (
            <div className="form-group">
              <label htmlFor="accountDirector">
                Account Director{accountDirectorRequired ? "" : " (optional)"}
              </label>

              <Select
                key={`account-director-options-key-${JSON.stringify(
                  state.availableAccountDirectors
                )}`}
                name="accountDirector"
                className="base-select"
                classNamePrefix="select"
                options={state.availableAccountDirectors}
                placeholder="Account Director"
                onChange={(selectedAD) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "selectedAccountDirector",
                    value: selectedAD,
                  });
                }}
              ></Select>
            </div>
          )}

        {showParentUsersField && (
            <div className="form-group">
              <label htmlFor="parentUsers">Parent Users</label>

              <Select
                key={`account-director-options-key-${JSON.stringify(
                  state.availableAccountDirectors
                )}`}
                name="parentUsers"
                className="base-select"
                classNamePrefix="select"
                isMulti
                options={state.availableAccountDirectors}
                placeholder="Account Director"
                onChange={(selectedAD) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "parentUsers",
                    value: selectedAD,
                  });
                }}
              ></Select>
            </div>
          )}

        {isEvoOrg && (
          <div className="form-group">
            <label htmlFor="outreachUserId">
              Outreach User{outreachUserRequired ? "" : " (optional)"}
            </label>

            <Select
              key={`account-director-options-key-${JSON.stringify(
                state.availableOutreachUsers
              )}`}
              name="outreachUserId"
              className="base-select"
              classNamePrefix="select"
              options={state.availableOutreachUsers}
              placeholder="Outreach User"
              onChange={(selectedOutreachUser) => {
                dispatch({
                  type: "fieldChanged",
                  field: "selectedOutreachUser",
                  value: selectedOutreachUser,
                });
              }}
            ></Select>
          </div>
        )}

        <input
          type="submit"
          value={state.isSubmitting ? "Saving..." : "Save New User"}
          disabled={state.isSubmitting}
        ></input>
      </form>

      {/* <pre>{JSON.stringify(state, null, 2)}</pre> */}
    </Fragment>
  );
};

export default NewUserContent;
